import React, { useState } from 'react';
import { Dialog } from '../common/Dialog';
import { Button } from '../common/Button';
import { TermsDialog } from './TermsDialog';
import { PrivacyDialog } from './PrivacyDialog';
import { ReleaseNotesDialog } from './ReleaseNotesDialog';
import * as styles from './AboutDialog.css.ts';
import packageInfo from '../../../../package.json';

interface AboutDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AboutDialog: React.FC<AboutDialogProps> = ({ isOpen, onClose }) => {
  const currentYear = new Date().getFullYear();
  const commitHash = import.meta.env.VITE_GIT_COMMIT;
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showReleaseNotes, setShowReleaseNotes] = useState(false);

  const footer = (
    <Button variant="primary" onClick={onClose}>
      OK
    </Button>
  );

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="About MIDAS"
      width="md"
      footer={footer}
    >
      <div
        className={styles.aboutContent}
      >
        {/* ロゴセクション */}
        <div className={styles.logoSection}>
          <div className={styles.logo}>M</div>
          <div className={styles.appInfo}>
            <h3 className={styles.appName}>MIDAS</h3>
          </div>
        </div>

        {/* バージョン情報 */}
        <div className={styles.versionSection}>
          <div className={styles.versionItem}>
            <span className={styles.label}>Version:</span>
            <span className={styles.value}>{packageInfo.version}</span>
          </div>
          {commitHash && commitHash !== 'unknown' && (
            <div className={styles.versionItem}>
              <span className={styles.label}>Build:</span>
              <span className={styles.value}>{commitHash}</span>
            </div>
          )}
        </div>

        {/* 説明 */}
        <div className={styles.descriptionSection}>
          <p>
            MIDAS is a web-based data analysis application that runs entirely
            in your browser. It provides tools for exploratory data analysis
            and visualization.
          </p>
        </div>

        {/* 機能リスト */}
        <div className={styles.featuresSection}>
          <h4>Key Features:</h4>
          <ul className={styles.featureList}>
            <li>Privacy-first: All data processing happens locally in your browser</li>
            <li>Support for CSV, TSV, and MDS (MIDAS project) files</li>
            <li>Interactive data tables with sorting and filtering</li>
            <li>Statistical analysis and visualization tools</li>
            <li>Report generation and export capabilities</li>
          </ul>
        </div>

        {/* 著作権情報 */}
        <div className={styles.legalSection}>
          <p className={styles.copyright}>
            © {currentYear} Izuru Matsuura. All rights reserved.
          </p>
          <p className={styles.legalLinks}>
            <a
              href="/docs/ja/index.html"
              className={styles.legalLink}
            >
              Documentation (ja)
            </a>
            {' • '}
            <a
              onClick={(e) => {
                e.preventDefault();
                setShowReleaseNotes(true);
              }}
              className={styles.legalLink}
              style={{ cursor: 'pointer' }}
            >
              Release Notes
            </a>
            {' • '}
            <a
              onClick={(e) => {
                e.preventDefault();
                setShowTerms(true);
              }}
              className={styles.legalLink}
              style={{ cursor: 'pointer' }}
            >
              Terms of Service
            </a>
            {' • '}
            <a
              onClick={(e) => {
                e.preventDefault();
                setShowPrivacy(true);
              }}
              className={styles.legalLink}
              style={{ cursor: 'pointer' }}
            >
              Privacy Policy
            </a>
          </p>
        </div>
      </div>

      {/* Release Notes Dialog */}
      <ReleaseNotesDialog
        isOpen={showReleaseNotes}
        onClose={() => setShowReleaseNotes(false)}
      />

      {/* Terms of Service Dialog */}
      <TermsDialog
        isOpen={showTerms}
        onClose={() => setShowTerms(false)}
      />

      {/* Privacy Policy Dialog */}
      <PrivacyDialog
        isOpen={showPrivacy}
        onClose={() => setShowPrivacy(false)}
      />
    </Dialog>
  );
};